var _excluded = ["id", "items", "spacing", "align", "flex", "utils"],
  _excluded2 = ["id", "item", "divider"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import generateID from './../../utilities/generateID';
import StyledHorizontalList from './horizontal-list.styles';
import StyledHorizontalListItem from './styles/horizontal-list.item.styles';
import isEqual from 'lodash/isEqual';
var horizontalListPropTypes = {
  /** ID of the component in order to put an unique identifier for the unordered list */
  id: PropTypes.string,
  /** An array of items that to be rendered as a list. */
  items: PropTypes.arrayOf(PropTypes.shape({
    /** Passing id is strongly recommended with the item array */
    id: PropTypes.string,
    /** The item to be rendered. */
    item: PropTypes.any,
    /** Adds a divider to a list item. Options: left or right. */
    divider: PropTypes.string
  })),
  /** The amount of spacing between each list item. accepts a values 2-10. */
  spacing: PropTypes.oneOf(['', '2', '3', '4', '5', '6', '7', '8', '9', '10']),
  /** Aligns the list items. Options: left (default), right, bottom, middle or center */
  align: PropTypes.oneOf(['left', 'right', 'middle', 'bottom', 'center']),
  /** Adds a modifier class that applies `display: flex` to the list. */
  flex: PropTypes.bool,
  /** Add utility props for styling */
  utils: PropTypes.object,
  /** Gives Horizontal List a performance boast by only re-rendering if the items change */
  isMemoized: PropTypes.bool
};
var horizontalListDefaultProps = {
  id: '',
  items: [{
    id: '1',
    item: 'List Item'
  }, {
    id: '2',
    item: 'List Item'
  }, {
    id: '3',
    item: 'List Item'
  }],
  spacing: '',
  align: 'left',
  flex: false,
  utils: {},
  isMemoized: false
};

/**
 * @ignore @description
 * ## HorizontalList Component
 * The HorizontalList layout component provides horizontal rhythm and alignment to its children. By default, <code>8px</code> of horizontal rhythm is applied between its children.
 *
 * @ignore @see Mesh: [HorizontalList Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/scaffolding-horizontallist--default)
 *
 * @ignore @example
 * ```jsx
 * import { HorizontalList } from 'mesh-component-library';
 *
 * <HorizontalList {...props}/>
 *
 * ```
 */

var HorizontalList = function HorizontalList(props) {
  var id = props.id,
    items = props.items,
    spacing = props.spacing,
    align = props.align,
    flex = props.flex,
    utils = props.utils,
    other = _objectWithoutProperties(props, _excluded);
  var useId = id || useRef(generateID('horizontal-list-')).current;
  return /*#__PURE__*/React.createElement(StyledHorizontalList, _extends({
    id: useId,
    align: align,
    flex: flex,
    utils: utils
  }, other), items.map(function (_ref, i) {
    var itemId = _ref.id,
      item = _ref.item,
      divider = _ref.divider,
      otherProps = _objectWithoutProperties(_ref, _excluded2);
    var key = itemId || "".concat(useId, "-key-").concat(i);
    return /*#__PURE__*/React.createElement(StyledHorizontalListItem, _extends({
      key: key,
      id: key,
      spacing: spacing,
      divider: divider
    }, otherProps), item);
  }));
};
HorizontalList.propTypes = horizontalListPropTypes;
HorizontalList.defaultProps = horizontalListDefaultProps;
function areItemsEqual(prevProps, nextProps) {
  return isEqual(prevProps.items, nextProps.items) && prevProps.isMemoized;
}
export default /*#__PURE__*/React.memo(HorizontalList, areItemsEqual);